import { useTranslation } from "react-i18next";
import {
  Invitation,
  Organization,
} from "../../../services/organization/organizationModel";
import { User } from "../../../services/user/userModel";
import {
  getOrganizationMemberStatus,
  ORGANIZATION_MEMBER_STATUS_LABEL_KEYS,
  OrganizationMemberStatus,
  organizationService,
} from "../../../services/organization/organizationService";
import { Dispatch, SetStateAction } from "react";
import { cx } from "../../../react-helpers/css";
import SortableTh from "../../../components/SortableTh";
import { TableSort } from "../../../react-helpers/array";
import { authService } from "../../../services/auth/authService";
import { useConfirmationWithIntl } from "../../../components/ConfirmationDialog";
import useReload from "../../../hooks/useReload";

const { getLoggedUserId } = authService();
const { deleteInvitation, removeUserFromOrganization } = organizationService();

export type UsersSortKey = "name" | "status";

const STATUS_CHIP_MODIFIERS: Record<OrganizationMemberStatus, string> = {
  active: "--ok",
  pending: "--warn",
  disabled: "--off",
};

// Puce de statut d'un membre, partagée avec la fenêtre « MAJ utilisateur » :
// la liste et la fenêtre doivent montrer le même statut de la même façon.
export const UserStatusChip = ({
  status,
}: {
  status?: OrganizationMemberStatus | null;
}) => {
  const { t } = useTranslation(["organization"]);

  return status ? (
    <span className={cx(["ui-chip", STATUS_CHIP_MODIFIERS[status]])}>
      {t(ORGANIZATION_MEMBER_STATUS_LABEL_KEYS[status])}
    </span>
  ) : null;
};

const AVATAR_COLORS = [
  "#0C0557",
  "#3452FF",
  "#C2410C",
  "#7A2E5A",
  "#1E6B5C",
  "#5B3FB0",
  "#9A3412",
  "#2B4C9B",
];

// Pastille d'initiales, couleur stable par personne (comme la maquette).
const UserAvatar = ({ user }: { user: User }) => {
  const initials =
    `${user.firstname?.[0] ?? ""}${user.lastname?.[0] ?? ""}` ||
    (user.email?.[0] ?? "");
  const hash = [...(user.email ?? `${user.firstname}${user.lastname}`)].reduce(
    (acc, char) => (acc * 31 + char.charCodeAt(0)) >>> 0,
    0,
  );

  return (
    <span
      className="admin-av"
      style={{ background: AVATAR_COLORS[hash % AVATAR_COLORS.length] }}
      aria-hidden="true"
    >
      {initials.toUpperCase()}
    </span>
  );
};

const UsersTable = ({
  organization,
  users,
  setInviteAndManageUser,
  sort,
  onSort,
}: {
  organization: Organization;
  users: (User & { invitation?: Invitation })[];
  setInviteAndManageUser: Dispatch<
    SetStateAction<{ user?: User; invitation?: Invitation } | null>
  >;
  sort: TableSort<UsersSortKey> | null;
  onSort: (key: UsersSortKey) => void;
}) => {
  const { t } = useTranslation(["organization"]);
  const { confirm } = useConfirmationWithIntl(["organization"]);
  const reload = useReload();

  return users.length > 0 ? (
    <div className="admin-tw">
      <table className="table admin-tbl">
        <thead>
          <tr>
            <SortableTh sortKey="name" sort={sort} onSort={onSort}>
              {t("organization:users.table-headers.NAME")}
            </SortableTh>
            <th>{t("organization:users.table-headers.ROLE")}</th>
            <SortableTh sortKey="status" sort={sort} onSort={onSort}>
              {t("organization:users.table-headers.STATUS")}
            </SortableTh>
            <th className="--num">
              {t("organization:users.table-headers.USED_CREDITS")}
            </th>
            <th className="--num">
              {t("organization:users.table-headers.ACTION")}
            </th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => {
            const userStatus = getOrganizationMemberStatus(
              organization.id,
              user,
            );

            return (
              <tr key={`user-${user.id}`}>
                <td className="--full">
                  <span className="admin-who">
                    <UserAvatar user={user} />
                    <span>
                      <b>
                        {user.firstname} {user.lastname}
                      </b>
                      <span>{user.email}</span>
                    </span>
                  </span>
                </td>
                <td data-label={t("organization:users.table-headers.ROLE")}>
                  {user.invitation?.membershipType === "GUEST"
                    ? t("organization:users.role.GUEST")
                    : user.adminOrga ||
                        user.invitation?.membershipType === "ADMIN"
                      ? t("organization:users.role.ADMIN")
                      : t("organization:users.role.MEMBER")}
                </td>
                <td data-label={t("organization:users.table-headers.STATUS")}>
                  <UserStatusChip status={userStatus} />
                </td>
                <td
                  className="--num"
                  data-label={t(
                    "organization:users.table-headers.USED_CREDITS",
                  )}
                >
                  {(user.capsules?.length ?? 0) > 0 && user.capsules?.length}
                </td>
                <td className="--num --full">
                  <div className="admin-actions">
                    <button
                      className="btn--soft --btn--s"
                      onClick={() => {
                        setInviteAndManageUser(
                          user.invitation
                            ? {
                                invitation: {
                                  ...user.invitation,
                                  firstname: user.firstname,
                                  lastname: user.lastname,
                                  email: user.email,
                                },
                              }
                            : {
                                user,
                              },
                        );
                      }}
                    >
                      <span className="admin-ic --edit" aria-hidden="true" />
                      {t("organization:users.EDIT_USER")}
                    </button>
                    {user.id !== getLoggedUserId() && (
                      <button
                        className="admin-iconbtn --danger"
                        title={t("organization:users.DELETE_USER_TIP")}
                        aria-label={t("organization:users.DELETE_USER_TIP")}
                        onClick={() => {
                          const invitation = user.invitation;
                          confirm(
                            userStatus === "pending"
                              ? "organization:users.confirm-delete.PENDING_MESSAGE"
                              : "organization:users.confirm-delete.MESSAGE",
                            () =>
                              (invitation
                                ? deleteInvitation(
                                    organization.id,
                                    invitation.id,
                                  )
                                : removeUserFromOrganization(
                                    organization.id,
                                    user.id,
                                  )
                              ).then(() => reload()),
                            true,
                            {
                              name: `${user.firstname} ${user.lastname}`,
                            },
                          );
                        }}
                      >
                        <span className="admin-ic --trash" aria-hidden="true" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  ) : (
    <div className="admin-tw">
      <div className="admin-empty">
        <span className="admin-empty_ill">
          <span className="admin-ic --search" aria-hidden="true" />
        </span>
        <b>{t("organization:users.NO_USERS_FOUND")}</b>
      </div>
    </div>
  );
};

export default UsersTable;
